import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

interface AddToCalendarProps {
  eventName: string;
  startDate: string; // ISO format: YYYY-MM-DD
  endDate: string; // ISO format: YYYY-MM-DD
  location: string;
  description?: string;
  hours?: string;
  className?: string;
}

export default function AddToCalendar({
  eventName,
  startDate,
  endDate,
  location,
  description,
  hours,
  className = '',
}: AddToCalendarProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Format dates for calendar links (YYYYMMDD format)
  const formatDateForCalendar = (dateString: string, isEndDate = false) => {
    const date = new Date(dateString);
    if (isEndDate) {
      // For end dates, add one day since calendar events are exclusive of end date
      date.setDate(date.getDate() + 1);
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  // Generate Google Calendar link
  const getGoogleCalendarUrl = () => {
    const start = formatDateForCalendar(startDate);
    const end = formatDateForCalendar(endDate, true);

    let details = description || `Visit ${eventName}`;
    if (hours) {
      details += `\n\nHours: ${hours}`;
    }

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: eventName,
      dates: `${start}/${end}`,
      details,
      location,
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Generate ICS file for Outlook/Apple Calendar
  const generateICSFile = () => {
    const start = formatDateForCalendar(startDate);
    const end = formatDateForCalendar(endDate, true);

    let details = description || `Visit ${eventName}`;
    if (hours) {
      details += `\\n\\nHours: ${hours}`;
    }

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Local Artist Fairs//Event//EN',
      'BEGIN:VEVENT',
      `UID:${Date.now()}@localartistfairs.com`,
      `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
      `DTSTART;VALUE=DATE:${start}`,
      `DTEND;VALUE=DATE:${end}`,
      `SUMMARY:${eventName}`,
      `DESCRIPTION:${details.replace(/\n/g, '\\n')}`,
      `LOCATION:${location}`,
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');

    return icsContent;
  };

  const handleDownloadICS = () => {
    const icsContent = generateICSFile();
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${eventName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setIsOpen(false);
  };

  const handleGoogleCalendar = () => {
    window.open(getGoogleCalendarUrl(), '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors font-medium flex items-center justify-center gap-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Calendar className="h-4 w-4" />
        <span>Add to Calendar</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-20">
            <button
              onClick={handleGoogleCalendar}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
            >
              <div className="flex items-center justify-center w-8 h-8 bg-moonbone rounded">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M5 1v4H1V1h4zm14 0v4h-4V1h4zM5 19v4H1v-4h4zm14 0v4h-4v-4h4zM5 7v10H1V7h4zm14 0v10h-4V7h4zM19 1v4h4V1h-4zM1 1v4h4V1H1zm18 18v4h4v-4h-4zM1 19v4h4v-4H1z" />
                </svg>
              </div>
              <div>
                <div className="font-medium text-gray-900">Google Calendar</div>
                <div className="text-xs text-gray-500">Opens in new tab</div>
              </div>
            </button>

            <button
              onClick={handleDownloadICS}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 border-t border-gray-100"
            >
              <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="font-medium text-gray-900">Outlook / Apple Calendar</div>
                <div className="text-xs text-gray-500">Download .ics file</div>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
